import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSiteExpensesQuery } from '../../../../hooks/queries/useSitesQuery';
import { SiteExpense } from '../../../../services/apiSLTP/sites/sites';
import { formatDateToYYYYMMDD } from '../../../../utils/dates';
import { localStorageKeys, systemConfigKeyEnum } from '../../../../utils/enums';
import { formatNumber } from '../../../../utils/numbers';
import GenericAgGridTable from '../../GenericAgGridTable';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import StateCell from '../../../cells/StateCell';
import { useSystemConfig } from '../../../../hooks/useSystemConfig';

interface SiteExpensesTableProps {
    startDate: Date;
    endDate: Date;
}

const SiteExpensesTable: React.FC<SiteExpensesTableProps> = ({ startDate, endDate }) => {
    const { t } = useTranslation();
    const { getConfigValue } = useSystemConfig();
    const { data, isLoading } = useSiteExpensesQuery({
        queries: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
    });

    const columnDefs: ColDef<SiteExpense>[] = [
        {
            headerName: capitalizeFirstLetter(t('conduc')),
            valueGetter: (params) => {
                return params.data?.siteConducUsername
                    ? {
                          username: params.data?.siteConducUsername || '',
                          color: params.data?.siteColor || '',
                      }
                    : null;
            },
            cellRenderer: (params: ICellRendererParams<SiteExpense>) => {
                if (!params.value) return null;
                return (
                    <StateCell
                        stateColor={params.value?.color}
                        text={params.value?.username}
                        showDot={false}
                        style={{ width: 60, justifyContent: 'center' }}
                    />
                );
            },
            valueFormatter: (params) => (params.value ? params.value.username : ''),
            filterValueGetter: (params) => {
                return params.data?.siteConducUsername;
            },
            keyCreator: (params) => params.value?._id,
            width: 150,
            colId: 'conduc',
        },

        {
            headerName: capitalizeFirstLetter(t('site')),
            field: 'siteName',
            filter: 'agTextColumnFilter',
            flex: 1,
            colId: 'siteName',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'siteCode',
            colId: 'siteCode',
        },
        {
            headerName: `${capitalizeFirstLetter(t('totalPrice'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            field: 'totalPrice',
            filter: 'agNumberColumnFilter',
            type: 'numericColumn',
            aggFunc: 'sum',
            colId: 'totalPrice',
            valueFormatter: (params) => formatNumber(params.value, t('locale'), 2),
        },
    ];

    return (
        <GenericAgGridTable<SiteExpense>
            rowData={data?.sites || []}
            columnDefs={columnDefs}
            loading={isLoading}
            exportCsvFileName={'expenses-' + formatDateToYYYYMMDD(startDate) + '-' + formatDateToYYYYMMDD(endDate)}
            storageKey={localStorageKeys.TABLE.OPERATIONS.EXPENSES.GLOBAL}
            agGridProps={{
                getRowId: ({ data }) => data.siteId,
                grandTotalRow: 'pinnedTop',
            }}
        />
    );
};

export default SiteExpensesTable;
